import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { cloneDeep } from 'lodash';
import { isEmpty } from 'lodash/lang';
import moment from 'moment';

import SitePaymentsApi from '../../../../api/finance/SitePaymentsApi';
import useSessionStorage from '../../../../common/hooks/useSessionStorage';
import NotificationManager from '../../../../common/notifications/NotificationManager';
import { SOMETHING_WENT_WRONG } from '../../../../constants/notificationMessages';
import { onFileSave, onRequestError } from '../../../../services/handlers';
import { studyTypeOptions } from '../shared/FinanceFilters/MultiSelectStudyType';

import { OPEN, PAID, PENDING } from './SitePaymentsTable/SitePaymentsConstants';

export const SitePaymentsContext = React.createContext(null);

export function SitePaymentsProvider({ children }) {
  const initialStartDate = useMemo(
    () => moment('2019-01-01').set({ hour: 0, minute: 0, second: 0, millisecond: 1 }),
    []
  );
  const initialEndDate = useMemo(() => moment().set({ hour: 23, minute: 59, second: 59, millisecond: 999 }), []);
  const emptyFilters = useMemo(
    () => ({
      site: null,
      study: null,
      pcn: null,
      finStudyTypes: studyTypeOptions,
      startDate: initialStartDate,
      endDate: initialEndDate
    }),
    [initialEndDate, initialStartDate]
  );

  const [savedFilters, setSavedFilters] = useSessionStorage('SITE_PAYMENTS_FILTERS', {});
  const initialFilters = isEmpty(savedFilters) ? emptyFilters : savedFilters;

  const [tableData, setTableData] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set([])
  });
  const [site, setSite] = useState(initialFilters.site);
  const [study, setStudy] = useState(initialFilters.study);
  const [pcn, setPcn] = useState(initialFilters.pcn);
  const [startDate, setStartDate] = useState(moment(initialFilters.startDate));
  const [endDate, setEndDate] = useState(moment(initialFilters.endDate));
  const [statuses, setStatuses] = useState([OPEN, PENDING, PAID]);

  const [filteredStudyTypes, setFilteredStudyTypes] = useState(savedFilters.finStudyTypes || studyTypeOptions);
  const [initialStudyTypes, setInitialStudyTypes] = useState(savedFilters.finStudyTypes || studyTypeOptions);

  const checkedEvents = useMemo(() => {
    return Array.from(rowSelectionModel.ids);
  }, [rowSelectionModel]);

  const filtersAssembler = useCallback(() => {
    return {
      studyName: study?.studyName,
      siteName: site?.siteName,
      studyId: study?.uniqueIdentifier,
      siteId: site?.uniqueIdentifier,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      statuses: statuses,
      projectCode: pcn,
      finStudyTypes: filteredStudyTypes
    };
  }, [
    study?.studyName,
    study?.uniqueIdentifier,
    site?.siteName,
    site?.uniqueIdentifier,
    startDate,
    endDate,
    statuses,
    pcn,
    filteredStudyTypes
  ]);

  const applyFilter = useCallback(() => {
    const filters = filtersAssembler();
    SitePaymentsApi.getSitePayments(filters).then(({ data }) => {
      setTableData(data);
      setSavedFilters({ site, study, pcn, startDate, endDate, finStudyTypes: filteredStudyTypes });
    });
  }, [endDate, filteredStudyTypes, filtersAssembler, pcn, setSavedFilters, site, startDate, study]);

  const resetFilters = useCallback(() => {
    setSite(null);
    setStudy(null);
    setPcn(null);
    setStartDate(initialStartDate);
    setEndDate(initialEndDate);
    setSavedFilters(emptyFilters);
    setInitialStudyTypes(studyTypeOptions);
    setFilteredStudyTypes(studyTypeOptions);
  }, [initialStartDate, initialEndDate, setSavedFilters, emptyFilters]);

  const onResetToOpen = useCallback(
    itemSiteIds => {
      SitePaymentsApi.resetToOpen(itemSiteIds)
        .then(() => {
          NotificationManager.success('Event(s) successfully reset to Open status');
          applyFilter();
        })
        .catch(() => {
          NotificationManager.error('Event(s) unable to be reset to Open status, please try again');
        });
    },
    [applyFilter]
  );

  const onApprove = useCallback(
    itemSiteIds => {
      SitePaymentsApi.setAsApproved(itemSiteIds)
        .then(() => {
          NotificationManager.success('Event(s) successfully Approved');
          applyFilter();
        })
        .catch(() => {
          NotificationManager.error('Event(s) unable to be Approved, please try again');
        });
    },
    [applyFilter]
  );

  const exportNS = useCallback(
    selectedEvents => {
      const siteItems = selectedEvents.map(item => ({
        sitePaymentBillNumber: item.sitePaymentBillNumber,
        itemSiteId: item.itemSiteId
      }));

      SitePaymentsApi.exportNS(siteItems)
        .then(onFileSave)
        .then(applyFilter)
        .catch(err => {
          if (err?.response?.status === 400) {
            const decodedString = String.fromCharCode.apply(null, new Uint8Array(err.response.data));
            const parsedError = JSON.parse(decodedString);
            NotificationManager.error(parsedError.message);
          } else {
            NotificationManager.error(SOMETHING_WENT_WRONG);
          }
        });
    },
    [applyFilter]
  );

  const updateBillNumberInTable = useCallback((billNumbers, itemSiteId) => {
    setTableData(prevData =>
      prevData.map(item =>
        item.itemSiteId === itemSiteId
          ? {
              ...item,
              sitePaymentBillNumber: billNumbers.sitePaymentBillNumber,
              siteBillNumber: billNumbers.siteBillNumber,
              siteBillDate: billNumbers.siteBillDate
            }
          : item
      )
    );
  }, []);

  useEffect(() => {
    if (!isEmpty(savedFilters)) {
      applyFilter();
    }
    // eslint-disable-next-line
  }, []);

  const normalizeFieldValue = useCallback((newRow, changedField) => {
    let columnValue = cloneDeep(newRow)[changedField];
    return moment(columnValue).isValid() ? moment(columnValue).format('YYYY-MM-DD') : null;
  }, []);

  const processRowUpdate = useCallback(
    async (newRow, oldRow) => {
      const changedField = Object.keys(newRow).find(key => newRow[key] !== oldRow[key]);
      const normalizedChangedFieldValue = normalizeFieldValue(newRow, changedField);
      if (!changedField || !normalizedChangedFieldValue || normalizedChangedFieldValue === oldRow[changedField]) {
        return oldRow;
      }

      try {
        const response = await SitePaymentsApi.updateSitePaymentDate(
          {
            sitePaymentDate: normalizedChangedFieldValue
          },
          newRow.itemSiteId
        ).catch(err => onRequestError(err));

        const updatedRow = {
          ...newRow,
          [changedField]: response.data.toString()
        };

        setTableData(prevState => prevState.map(row => (row.itemSiteId === newRow.itemSiteId ? updatedRow : row)));
        NotificationManager.success('Site Payment Date changed successfully');

        return updatedRow;
      } catch (error) {
        NotificationManager.error('Failed to update cell.');
        return oldRow;
      }
    },
    [normalizeFieldValue]
  );

  return (
    <SitePaymentsContext.Provider
      value={{
        setSite,
        setStudy,
        startDate,
        endDate,
        setStartDate,
        setEndDate,
        tableData,
        checkedEvents,
        rowSelectionModel,
        setRowSelectionModel,
        applyFilter,
        resetFilters,
        study,
        site,
        pcn,
        setPcn,
        statuses,
        setStatuses,
        onResetToOpen,
        onApprove,
        exportNS,
        updateBillNumberInTable,
        filteredStudyTypes,
        setFilteredStudyTypes,
        initialStudyTypes,
        setInitialStudyTypes,
        processRowUpdate
      }}
    >
      {children}
    </SitePaymentsContext.Provider>
  );
}

export function withSitePaymentsContext(Component) {
  return function WrapperComponent(props) {
    return (
      <SitePaymentsProvider>
        <Component {...props} />
      </SitePaymentsProvider>
    );
  };
}
